/**
 * EU-78 — query parameters read by Keycloak `register.js` / persisted as optional user attributes
 * (`gtm_*`). Append to the URL returned by `keycloak.createRegisterUrl()` before redirecting.
 *
 * Storage names: `opportunity*` = programa, `vacancy*` = vaga. No GA4/dataLayer a vaga aparece como
 * `career_*` — o rename acontece no protocol mapper do Keycloak (atributo `gtm_vacancy_*` → claim
 * `career_*`), mantendo intacto o contrato `gtm_*` consumido server-side (Mixpanel via BFF).
 */
import { getKeycloak } from './keycloak-instance';
import { markJustSignedUp } from '@/features/account/onboarding/shared/signup-welcome-flag';

export type EurecaGtmRegistrationContext = {
  opportunityId?: string;
  vacancyId?: string;
  clientName?: string;
  vacancyName?: string;
  opportunityName?: string;
};

const KEYS = {
  opportunityId: 'eureca_opportunity_id',
  vacancyId: 'eureca_vacancy_id',
  clientName: 'eureca_client_name',
  vacancyName: 'eureca_vacancy_name',
  opportunityName: 'eureca_opportunity_name',
} as const;

/** Same storage key / snake_case payload shape as Keycloak `register.js` (cross-page on the IdP). */
const REGISTRATION_CTX_STORAGE_KEY = 'eureca_gtm_registration_context';

type StoredRegistrationCtx = {
  opportunity_id: string;
  vacancy_id: string;
  client_name: string;
  vacancy_name: string;
  opportunity_name: string;
};

function pickParam(params: URLSearchParams, keys: string[]): string {
  for (const k of keys) {
    const v = params.get(k);
    if (v != null && v.trim() !== '') return v.trim();
  }
  return '';
}

function storedToAppContext(s: StoredRegistrationCtx): EurecaGtmRegistrationContext {
  return {
    opportunityId: s.opportunity_id || undefined,
    vacancyId: s.vacancy_id || undefined,
    clientName: s.client_name || undefined,
    vacancyName: s.vacancy_name || undefined,
    opportunityName: s.opportunity_name || undefined,
  };
}

function mergeGtmContext(
  persisted: EurecaGtmRegistrationContext,
  override?: Partial<EurecaGtmRegistrationContext>
): EurecaGtmRegistrationContext {
  if (!override) return persisted;
  const patch = Object.fromEntries(
    Object.entries(override).filter(([, v]) => v !== undefined && String(v).trim() !== '')
  ) as Partial<EurecaGtmRegistrationContext>;
  return { ...persisted, ...patch };
}

/** Parses current or given `location.search` and persists GTM dimensions (same keys as Keycloak theme). */
export function captureEurecaGtmRegistrationContextFromSearch(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): void {
  if (typeof window === 'undefined' || !search) return;
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const raw: StoredRegistrationCtx = {
    opportunity_id: pickParam(params, ['eureca_opportunity_id', 'opportunity_id']),
    vacancy_id: pickParam(params, ['eureca_vacancy_id', 'vacancy_id']),
    client_name: pickParam(params, ['eureca_client_name', 'client_name']),
    vacancy_name: pickParam(params, ['eureca_vacancy_name', 'vacancy_name']),
    opportunity_name: pickParam(params, ['eureca_opportunity_name', 'opportunity_name']),
  };
  const hasAny = Object.values(raw).some(Boolean);
  try {
    if (hasAny) {
      sessionStorage.setItem(REGISTRATION_CTX_STORAGE_KEY, JSON.stringify(raw));
    } else {
      sessionStorage.removeItem(REGISTRATION_CTX_STORAGE_KEY);
    }
  } catch {
    /* private mode */
  }
}

export function readPersistedEurecaGtmRegistrationContext(): EurecaGtmRegistrationContext {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(REGISTRATION_CTX_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<StoredRegistrationCtx>;
    return storedToAppContext({
      opportunity_id: String(parsed.opportunity_id ?? ''),
      vacancy_id: String(parsed.vacancy_id ?? ''),
      client_name: String(parsed.client_name ?? ''),
      vacancy_name: String(parsed.vacancy_name ?? ''),
      opportunity_name: String(parsed.opportunity_name ?? ''),
    });
  } catch {
    return {};
  }
}

/**
 * Redirects to Keycloak registration with EU-78 query params.
 * Merges URL-captured context (see `captureEurecaGtmRegistrationContextFromSearch`) with `override`.
 */
export async function openCandidateRegistration(
  override?: Partial<EurecaGtmRegistrationContext>
): Promise<void> {
  const merged = mergeGtmContext(readPersistedEurecaGtmRegistrationContext(), override);
  const keycloak = getKeycloak();
  const registerUrl = await keycloak.createRegisterUrl();
  const finalUrl = appendEurecaGtmRegistrationParams(registerUrl, merged);
  // EU-353: marca que esta sessão veio de um cadastro REAL, para a tela "Sua
  // conta foi criada!" só aparecer no retorno do registro — nunca num login de
  // conta existente. sessionStorage sobrevive ao round-trip pro Keycloak.
  markJustSignedUp();
  window.location.assign(finalUrl);
}

/** Adds non-empty GTM registration dimensions to a Keycloak registration URL. */
export function appendEurecaGtmRegistrationParams(
  registerUrl: string,
  context: EurecaGtmRegistrationContext
): string {
  let url: URL;
  try {
    url = new URL(registerUrl, window.location.origin);
  } catch {
    return registerUrl;
  }
  const entries: [keyof EurecaGtmRegistrationContext, string][] = [
    ['opportunityId', KEYS.opportunityId],
    ['vacancyId', KEYS.vacancyId],
    ['clientName', KEYS.clientName],
    ['vacancyName', KEYS.vacancyName],
    ['opportunityName', KEYS.opportunityName],
  ];
  for (const [field, param] of entries) {
    const v = context[field]?.trim();
    if (v) url.searchParams.set(param, v);
  }
  return url.toString();
}
